import { useMemo, type Dispatch, type ReactNode } from 'react';
import RichText from 'components/rich-text';
import TabDrawers from 'components/tab-drawer';
import CloseIcon from 'assets/icons/close-x.svg';
import AboutPanel from '../about-panel';
import { ScrollableContainer } from '../scrollable-container';
import styles from './mobile-info-panel.module.scss';
import type { ViewerAction } from '../../types';
import type { PortableTextBlock } from '@portabletext/types';

const NO_TRANSCRIPTION_MESSAGE = 'No transcription available.';
const NO_TRANSLATION_MESSAGE = 'No translation available.';

export default function MobileInfoPanel({
	showTranslation,
	showTranscription,
	pageIndex,
	transcription,
	translation,
	activeComment,
	dispatch,
	about,
	howToCite,
	showAboutPanel,
}: MobileInfoPanelProps) {
	const showReadingTools = showTranslation || showTranscription;

	let handleClose;
	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			handleClose();
		}
	};

	if (showAboutPanel) {
		handleClose = () => dispatch({ type: 'hide_about_panel' });

		return (
			<MobileInfoPanelContainer
				title="About"
				onKeyDown={handleKeyDown}
				onClose={handleClose}
			>
				<div className={styles.aboutPanelContents}>
					<AboutPanel about={about} howToCite={howToCite} />
				</div>
			</MobileInfoPanelContainer>
		);
	}

	if (activeComment) {
		handleClose = () => dispatch({ type: 'hide_shown_comment' });

		return (
			<MobileInfoPanelContainer onKeyDown={handleKeyDown} onClose={handleClose}>
				<MobileCommentContents comment={activeComment} />
			</MobileInfoPanelContainer>
		);
	}

	if (showReadingTools) {
		handleClose = () => dispatch({ type: 'hide_reading_tools' });

		return (
			<MobileInfoPanelContainer onKeyDown={handleKeyDown} onClose={handleClose}>
				<MobileReadingToolsContents
					forceRerenderKey={`page-${pageIndex}-mobile}`}
					transcription={transcription}
					translation={translation}
					initialActiveTab={showTranscription ? 'Transcription' : 'Translation'}
					dispatch={dispatch}
				/>
			</MobileInfoPanelContainer>
		);
	}

	return null;
}

function MobileInfoPanelContainer({
	title,
	children,
	onClose,
	onKeyDown,
}: MobileInfoPanelContainerProps) {
	return (
		<div className={styles.mobileInfoPanel}>
			<div className={styles.mobileInfoPanelHeader}>
				{title && <h2 className={styles.panelTitle}>{title}</h2>}
				<CloseIcon
					className={styles.panelCloseIcon}
					onClick={onClose}
					onKeyDown={onKeyDown}
					role="button"
					tabIndex={0}
				/>
			</div>
			{children}
		</div>
	);
}

function MobileCommentContents({ comment }: MobileCommentContentsProps) {
	const { content, citation } = comment;

	return (
		<div className={styles.commentContents}>
			<ScrollableContainer>
				<>
					<RichText value={content} />
					<span className={styles.commentCitation}>
						<RichText value={citation} />
					</span>
				</>
			</ScrollableContainer>
		</div>
	);
}

function MobileReadingToolsContents({
	transcription,
	translation,
	initialActiveTab,
	dispatch,
	forceRerenderKey,
}: MobileReadingToolsContentsProps) {
	const handleChange = (key: 'Transcription' | 'Translation') => {
		if (key === 'Transcription') {
			dispatch({ type: 'show_transcription' });
		} else if (key === 'Translation') {
			dispatch({ type: 'show_translation' });
		}
	};

	const tabs = useMemo(
		() => [
			{
				name: 'Transcription',
				body: (
					<ReadingToolTabBody
						content={transcription}
						emptyMessage={NO_TRANSCRIPTION_MESSAGE}
					/>
				),
			},
			{
				name: 'Translation',
				body: (
					<ReadingToolTabBody
						content={translation}
						emptyMessage={NO_TRANSLATION_MESSAGE}
					/>
				),
			},
		],
		[transcription, translation],
	);

	return (
		<TabDrawers
			// TODO: Figure out why we have to force a rerender of the tabs here on page change to get the content
			// to update. Currently, changing the page from the "All Spreads" view does not update the tab
			// bodies until another interaction causes a rerender.
			key={forceRerenderKey}
			hasDivider={false}
			tabs={tabs}
			inputGroupName={'reading-tools-tabs'}
			screenreaderLegendText="View tools to help read this document"
			tabNameAlignment="start"
			tabBodyWrapperClass={styles.tabBodyWrapper}
			initialActiveTab={initialActiveTab}
			onTabChange={handleChange}
		/>
	);
}

function ReadingToolTabBody({
	content,
	emptyMessage,
}: ReadingToolTabBodyProps) {
	return (
		<ScrollableContainer>
			{content?.length ? (
				<RichText value={content} />
			) : (
				<p className={styles.panelEmptyMessage}>{emptyMessage}</p>
			)}
		</ScrollableContainer>
	);
}

interface MobileInfoPanelProps {
	showTranscription: boolean;
	showTranslation: boolean;
	showAboutPanel: boolean;
	pageIndex: number;
	activeComment: {
		x: number;
		y: number;
		content: PortableTextBlock[];
		citation: PortableTextBlock[];
	} | null;
	dispatch: Dispatch<ViewerAction>;
	transcription?: PortableTextBlock[];
	translation?: PortableTextBlock[];
	about?: PortableTextBlock[];
	howToCite?: string;
}

interface MobileInfoPanelContainerProps {
	title?: string;
	children: ReactNode;
	onClose: () => void;
	onKeyDown: (e: KeyboardEvent) => void;
}

interface MobileCommentContentsProps {
	comment: {
		x: number;
		y: number;
		content: PortableTextBlock[];
		citation: PortableTextBlock[];
	};
}

interface MobileReadingToolsContentsProps {
	initialActiveTab: 'Transcription' | 'Translation';
	translation?: PortableTextBlock[];
	transcription?: PortableTextBlock[];
	forceRerenderKey: string;
	dispatch: Dispatch<ViewerAction>;
}

interface ReadingToolTabBodyProps {
	emptyMessage?: string;
	content?: PortableTextBlock[];
}
